import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';
import { CheckCircle2, ArrowRight, Menu } from 'lucide-react';
import { toast } from 'react-toastify';
import Spinner from 'components/common/Spinner';
import { validateTokenWithServer, getAccessToken } from 'utils/auth/auth';
import { AppDispatch } from 'config/redux/store';

const PaymentSuccess: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();

  // Color tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#94A3B8' : '#64748B';

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      toast.error(t('payment.errors.noSessionId') || 'No session ID found');
      setTimeout(() => navigate('/subscriptions'), 2000);
      return;
    }

    // After Stripe redirects here, the webhook needs ~1-3s to fire on the backend
    // and flip user.expired=false. Re-validate the token to refresh Redux state
    // (isExpired, planName, etc.) before showing the success screen.
    const verifyPayment = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 1500));
        const token = getAccessToken();
        if (token) {
          await validateTokenWithServer(token, dispatch);
        }
        setVerifying(false);
        toast.success(t('payment.success.message') || 'Payment successful! Your subscription has been upgraded.');
      } catch (error) {
        console.error('Payment verification error:', error);
        toast.error(t('payment.errors.verificationFailed') || 'Failed to verify payment');
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [searchParams, navigate, t, dispatch]);

  const handleContinue = () => {
    window.location.href = '/dashboard';
  };

  if (verifying) {
    return <Spinner size="large" text={t('payment.verifying') || 'Verifying payment...'} fullPage />;
  }

  return (
    <div
      className="flex flex-col items-center justify-center flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{ background: containerBg }}
    >
      <div
        className="rounded-none lg:rounded-2xl"
        style={{
          background: mainBg,
          boxShadow: mainShadow,
          maxWidth: '480px',
          width: '100%',
          padding: '48px 40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {/* Hamburger menu (mobile) */}
        <button
          className="lg:hidden flex items-center justify-center flex-shrink-0 self-start"
          style={{ width: '36px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, marginBottom: '8px' }}
          onClick={toggleSidebar}
        >
          <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
        </button>

        {/* Success Icon */}
        <div
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: isDark ? 'rgba(34, 197, 94, 0.1)' : '#F0FDF4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
          }}
        >
          <CheckCircle2 size={28} color={isDark ? '#4ADE80' : '#22C55E'} />
        </div>

        {/* Title */}
        <h2
          style={{
            fontSize: '20px',
            fontWeight: 700,
            color: titleColor,
            marginBottom: '8px',
            textAlign: 'center',
          }}
        >
          {t('payment.success.title') || 'Payment Successful!'}
        </h2>

        {/* Description */}
        <p
          style={{
            fontSize: '13px',
            color: subtitleColor,
            textAlign: 'center',
            lineHeight: '1.6',
            marginBottom: '28px',
            maxWidth: '360px',
          }}
        >
          {t('payment.success.description') || 'Your subscription has been upgraded successfully. You can now enjoy all the features of your new plan.'}
        </p>

        {/* Continue Button */}
        <button
          onClick={handleContinue}
          style={{
            width: '100%',
            padding: '12px',
            background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
            color: '#FFFFFF',
            borderRadius: '10px',
            border: 'none',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <ArrowRight size={16} />
          {t('payment.success.continue') || 'Continue to Dashboard'}
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccess;
